import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  HostListener,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { SectionComponent } from '../../../../components/layout/section/section.component';
import { SectionHeadlineComponent } from '../../../../components/layout/section/section-header/section-headline/section-headline.component';
import { CmsTextProviderService } from '../../../../services/cms-text-provider.service';
import {
  ICmsTicketOverviewItem,
  ETicketStatusFilter,
  ETicketType,
  ISearchSortimentForm,
  TicketSortOption,
  TicketSortValue,
  ticketStatuses,
  ticketTypes,
} from '@web/dtos';
import { TicketSortButtonComponent } from '../../../../components/data-display/ticket-sort-button/ticket-sort-button.component';
import { SearchBarComponent } from '../../../../components/data-input/form-inputs/search-bar/search-bar.component';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { DOCUMENT } from '@angular/common';
import { MultiSelectDropDownComponent } from '../../../../components/data-input/form-inputs/multi-select-drop-down/multi-select-drop-down.component';
import { DropDownComponent } from '../../../../components/data-input/form-inputs/drop-down/drop-down.component';

@Component({
  selector: 'aw-sortiment-section',
  imports: [
    SectionComponent,
    SectionHeadlineComponent,
    SearchBarComponent,
    TicketSortButtonComponent,
    ReactiveFormsModule,
    MultiSelectDropDownComponent,
    DropDownComponent,
  ],
  templateUrl: './sortiment-section.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SortimentSectionComponent {
  readonly isLoading = input(false);
  readonly hasError = input(false);
  readonly retry = output<void>();
  private openMenu?: { panel: HTMLElement; anchor: HTMLElement };

  constructor() {
    const document = inject(DOCUMENT);
    const reposition = () => this.positionOpenMenu();
    document.addEventListener('scroll', reposition, true);
    inject(DestroyRef).onDestroy(() => {
      document.removeEventListener('scroll', reposition, true);
    });
  }

  protected onMenuToggle(
    event: Event,
    anchor: HTMLElement,
    menu: 'sort' | 'filter'
  ): void {
    const panel = event.target as HTMLElement;
    const isOpen = panel.matches(':popover-open');
    (menu === 'sort' ? this.isSortMenuOpen : this.isFilterMenuOpen).set(isOpen);
    if (isOpen) {
      this.openMenu = { panel, anchor };
      this.positionOpenMenu();
    } else if (this.openMenu?.panel === panel) {
      this.openMenu = undefined;
    }
  }

  @HostListener('window:resize')
  protected positionOpenMenu(): void {
    if (!this.openMenu) return;
    const { panel, anchor } = this.openMenu;
    const rect = anchor.getBoundingClientRect();
    const margin = 8;
    const gap = 4;
    panel.style.maxHeight = `${Math.max(0, window.innerHeight - margin * 2)}px`;
    const left = Math.max(
      margin,
      Math.min(rect.left, window.innerWidth - panel.offsetWidth - margin)
    );
    const below = rect.bottom + gap;
    const above = rect.top - gap - panel.offsetHeight;
    const top =
      below + panel.offsetHeight <= window.innerHeight - margin
        ? below
        : Math.max(margin, above);
    panel.style.left = `${left}px`;
    panel.style.top = `${top}px`;
  }

  public tickets: InputSignal<ICmsTicketOverviewItem[]> =
    input.required<ICmsTicketOverviewItem[]>();
  public readonly selectedTicketId: InputSignal<string | null> = input.required<
    string | null
  >();
  public readonly editedTicketIds: InputSignal<ReadonlySet<string>> =
    input.required<ReadonlySet<string>>();
  public readonly completedLayoutTicketIds: InputSignal<ReadonlySet<string>> =
    input.required<ReadonlySet<string>>();
  public readonly ticketSelected: OutputEmitterRef<string> = output<string>();
  public readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );
  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  public readonly filterForm: FormGroup<ISearchSortimentForm> = new FormGroup({
    search: this.formBuilder.control<string | null>(null),
    sortedBy: this.formBuilder.control<string | null>(null),
    filterType: this.formBuilder.nonNullable.control<ETicketType[]>([]),
    filterStatus: this.formBuilder.nonNullable.control<
      ETicketStatusFilter | ''
    >(''),
  });

  private readonly searchValue: Signal<string | null> = toSignal(
    this.filterForm.controls.search.valueChanges,
    { initialValue: this.filterForm.controls.search.value }
  );
  private readonly ticketTypeValues: Signal<ETicketType[]> = toSignal(
    this.filterForm.controls.filterType.valueChanges,
    { initialValue: this.filterForm.controls.filterType.value }
  );
  private readonly ticketStatusValue: Signal<ETicketStatusFilter | ''> =
    toSignal(this.filterForm.controls.filterStatus.valueChanges, {
      initialValue: this.filterForm.controls.filterStatus.value,
    });
  protected readonly isFilterMenuOpen: WritableSignal<boolean> = signal(false);
  protected readonly isSortMenuOpen: WritableSignal<boolean> = signal(false);
  protected readonly selectedSort: WritableSignal<TicketSortValue> =
    signal('type-descending');
  protected readonly sortOptions: TicketSortOption[] = [
    {
      label: this.cmsTextProvider.sortiment.nameDescending,
      value: 'name-descending',
    },
    {
      label: this.cmsTextProvider.sortiment.nameAscending,
      value: 'name-ascending',
    },
    {
      label: this.cmsTextProvider.sortiment.typeDescending,
      value: 'type-descending',
    },
    {
      label: this.cmsTextProvider.sortiment.typeAscending,
      value: 'type-ascending',
    },
  ];

  public readonly filteredTickets: Signal<ICmsTicketOverviewItem[]> = computed(
    (): ICmsTicketOverviewItem[] => {
      const searchString: string =
        this.searchValue()?.trim().toLocaleLowerCase('de') ?? '';
      const selectedTicketTypes: ETicketType[] = this.ticketTypeValues();
      const selectedTicketStatus: ETicketStatusFilter | '' =
        this.ticketStatusValue();

      const filteredTickets: ICmsTicketOverviewItem[] = this.tickets().filter(
        (ticket: ICmsTicketOverviewItem): boolean => {
          const matchesSearch: boolean =
            !searchString ||
            ticket.name.toLocaleLowerCase('de').includes(searchString);
          const ticketType: ETicketType | undefined = this.ticketTypeFromLabel(
            ticket.ticketType
          );
          const matchesType: boolean =
            selectedTicketTypes.length === 0 ||
            (!!ticketType && selectedTicketTypes.includes(ticketType));
          const matchesStatus: boolean =
            !selectedTicketStatus ||
            (selectedTicketStatus === ETicketStatusFilter.Active
              ? ticket.ticketData?.isActive === true
              : ticket.ticketData?.isActive !== true);

          return matchesSearch && matchesType && matchesStatus;
        }
      );

      return [...filteredTickets].sort(
        (
          firstTicket: ICmsTicketOverviewItem,
          secondTicket: ICmsTicketOverviewItem
        ): number => {
          switch (this.selectedSort()) {
            case 'name-ascending':
              return firstTicket.name.localeCompare(secondTicket.name, 'de');
            case 'name-descending':
              return secondTicket.name.localeCompare(firstTicket.name, 'de');
            case 'type-ascending':
              return firstTicket.ticketType.localeCompare(
                secondTicket.ticketType,
                'de'
              );
            case 'type-descending':
              return secondTicket.ticketType.localeCompare(
                firstTicket.ticketType,
                'de'
              );
          }
        }
      );
    }
  );

  protected selectSort(value: TicketSortValue): void {
    this.selectedSort.set(value);
    this.isSortMenuOpen.set(false);
  }

  protected readonly ticketTypes: typeof ticketTypes = ticketTypes;
  protected readonly ticketStatuses: typeof ticketStatuses = ticketStatuses;

  private ticketTypeFromLabel(ticketType: string): ETicketType | undefined {
    switch (ticketType) {
      case 'Festpreis':
        return ETicketType.Direct;
      case 'Abonnement':
        return ETicketType.Subscription;
      case 'Relation':
        return ETicketType.Relation;
      default:
        return undefined;
    }
  }
}
